/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form,
  Button,
  Row,
  Col,
  Spin,
  Input,
  Modal,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';

import { checkToken } from 'api/utils';
import {
  SLUG_PATTERN,
} from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { CheckCircleOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';
import {
  deleteProyectoPresupuestal,
  getProyectoPresupuestal,
  getProyectoPresupuestales,
  patchProyectoPresupuestal,
  postProyectoPresupuestal,
} from 'api/catalogos/ProyectoPresupuestal';

export const motivoReclutamientoPermission = {
  permissionModel: 'proyectopresupuestal',
  catalogsModels: [],
  tabsModels: [],
};

const ProyectosPresupuestales = ({ permission }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState();
  const [open, setVisible] = useState();
  const [visibleAlert, setVisibleAlert] = useState();
  const [estados, setEstados] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);
  const [form] = Form.useForm();

  const formInstances = [
    form,
  ];

  const fetchData = async () => {
    setLoading(true);
    const _data = await getProyectoPresupuestales();
    setData(formatReceived(_data));
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      setLoading(true);
      await checkToken();
      const [
        _estados,
        _data,
      ] = await Promise.all([
        getEstadosGlobales(),
        getProyectoPresupuestales(),
      ]);
      if (mounted) {
        setEstados(_estados);
        setData(formatReceived(_data));
        setLoading(false);
      }
    };
    fetchAll();
    return () => { mounted = false; };
  }, []);

  const onCancel = () => {
    setSelectedRowKeys([]);
    setDisableForm(false);
    setVisible(false);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess({}, 'No se puede Autorizar un Proyecto Presupuestal ya Autorizado');
        return;
      }
      const vals = {
        ...form.getFieldValue(),
        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await patchProyectoPresupuestal(key, vals);
      if (res) {
        await fetchData();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    if (rowEstatus !== ESTATUS_ACTIVO) {
      setDisableForm(true);
    }
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || data.find((item) => item.id === key);
    const _motivoPermiso = await getProyectoPresupuestal(match.id);
    setTimeout(() => {
      if (_motivoPermiso) {
        form.resetFields();
        setTimeout(() => {
          isDisabled(_motivoPermiso?.estados_globales);
          form.setFieldsValue(formatReceived(_motivoPermiso));
          setVisible(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchProyectoPresupuestal(key, values)
        : postProyectoPresupuestal(values));
      if (saved) {
        if (_continue) {
          await fetchData();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(
        err,
        setLoading,
        formInstances,
      );
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteProyectoPresupuestal(key);
        if (deleted) {
          setVisibleAlert(false);
          onCancel();
          await fetchData();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    form.resetFields();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const columns = [
    {
      titleText: 'Clave',
      dataIndex: 'clave',
      key: 'clave',
      width: 100,
    },
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 250,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  const requiredRule = {
    required: true,
    message: 'El campo es requerido',
  };

  const rules = {
    required: [requiredRule],
    contratacion: [requiredRule],
    estatus: [requiredRule],
    clave: [
      requiredRule,
      {
        pattern: SLUG_PATTERN,
        message: 'Ingrese una clave válida',
      },
    ],
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={data}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit: () => onClickEdit(),
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          customActions={[
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'proyectopresupuestal', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={
            deleteDisabled
          }
        />
        <Modal
          open={open}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Motivo de Permiso`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            onFinish={onFinish}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="clave"
                rules={rules.clave}
                label="Clave"
                hasFeedback
              >
                <Input
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="descripcion"
                label="Descripción"
                rules={rules.required}
                hasFeedback
              >
                <Input
                  allowClear
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                rules={rules.estatus}
                hasFeedback
                initialValue={ESTATUS_ACTIVO}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  activeInactive={!deleteDisabled}
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setVisibleAlertInactive(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setVisibleAlert(false)}
          open={visibleAlert}
          content={`Motivo Permiso ${form.getFieldValue('concepto')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar el Proyecto Presupuestal ${form.getFieldValue('clave')}`}
          loading={loading}
        />
        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar el Proyecto Presupuestal ${form.getFieldValue('clave')}`}
          loading={loading}
        />
      </Spin>
    </Row>
  );
};

export default ProyectosPresupuestales;
